import React, { useCallback, useState } from 'react'
import { Stack } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import ProcessPaletteSection from './ProcessPaletteSection'
import { LibraryScrollList, LibrarySearchField } from './LibraryListParts'
import { libraryPageRequest, useDebouncedTerm, usePagedLibraryList } from './usePagedLibraryList'

/** #152 S2 · RN-01.2: templates are searched by code, name and DESCRIPTION, on the server. */
const TEMPLATE_SEARCH_COLUMNS = JSON.stringify({ name: ['code', 'description'] })

/**
 * ProcessLibraryList — the Processes family (story 167, HU-04): the templates `getWorkflowTemplates` lists
 * today (same list, same order by code and version, `WorkflowFlow.jsx:113-122`), 20 at a time as the user
 * scrolls (D12, D16.8), each one unfoldable into its process tree.
 */
const ProcessLibraryList = ({
  active = false,
  fetchTemplatePage,
  canReadOperationLibrary = false,
  census = undefined,
  pendingOnly = false,
  showDuration = false,
  chosenKey = '',
  onChoose,
  onItemDragStart,
  onItemDragEnd
}) => {
  const { t_ } = useSmartTranslation()
  const [searchText, setSearchText] = useState('')
  const term = useDebouncedTerm(searchText)
  const fetchPage = useCallback(
    (page) => fetchTemplatePage(libraryPageRequest({ page, term, mixedColumnFilters: TEMPLATE_SEARCH_COLUMNS })),
    [fetchTemplatePage, term]
  )
  const list = usePagedLibraryList({ fetchPage, term, active })
  const loadNextPage = list.loadNext
  return (
    <Stack data-testid='library-processes' spacing={1} sx={{ flex: '1 1 0', minHeight: 0 }}>
      <LibrarySearchField testId='library-search-processes' value={searchText} onChange={setSearchText} placeholder={t_('Search a process…')} />
      <LibraryScrollList
        testId='library-list-processes'
        loading={list.loading}
        failed={list.failed}
        failureText={list.failureMessage || t_('The processes could not be loaded')}
        empty={list.items.length === 0}
        pageFailed={list.pageFailed}
        pageFailureText={list.pageFailureMessage || t_('The processes could not be loaded')}
        onReachEnd={loadNextPage}
      >
        {list.items.map((process) => (
          <ProcessPaletteSection
            key={process.id}
            process={process}
            canReadOperationLibrary={canReadOperationLibrary}
            showDuration={showDuration}
            census={census}
            pendingOnly={pendingOnly}
            chosenKey={chosenKey}
            onChoose={onChoose}
            onItemDragStart={onItemDragStart}
            onItemDragEnd={onItemDragEnd}
          />
        ))}
      </LibraryScrollList>
    </Stack>
  )
}

export default ProcessLibraryList
